import React, { useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Button from "../../components/utils/Button";
import Table from "../../components/utils/Table";
import CardLayout from "../../components/utils/CardLayout";
import { useDispatch, useSelector } from "react-redux";
import reportActions from "../../redux/reports/actions";
import genericActions from "../../redux/generic/actions";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import moment from "moment";

const { getACode } = genericActions;
const { getFundManagementReports, resetMessage } = reportActions;
const dateFormat = process.env.REACT_APP_DATE_TIME_FORMAT;


const FundManagementReport = () => {
  const dispatch = useDispatch();
  const { aCodeList } = useSelector(state => state.generic);
  const [searchItem, setSearchItem] = useState("");
  const today = new Date();
  const beforeTwoDays = new Date();
  beforeTwoDays.setDate(beforeTwoDays.getDate() - 2);
  const formatDate = (date) => date.toISOString().split("T")[0];
  const { fundManagementReport, transactionReportsLoading, } = useSelector((state) => state.reports)
  const { defaultCurrency } = useSelector((state) => state.auth);


  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: {
      distributorId: 0,
      txnId: "",
      fromDate: formatDate(beforeTwoDays),
      toDate: formatDate(today),

    },
  });


  const getActivationReportFun = () => {
    dispatch(getFundManagementReports({
      distributorId: 0,
      txnId: "",
      fromDate: formatDate(beforeTwoDays),
      toDate: formatDate(today),
    }));
  }

  useEffect(() => {
    dispatch(getACode());
    getActivationReportFun()
  }, [dispatch]);


  const columns = [
    { displayName: "ACode", key: "aCode" },
    { displayName: "Dealer ID", key: "dealerId" },
    { displayName: "Transaction ID", key: "txnid" },
    { displayName: "Transaction Type", key: "transactionType" },
    { displayName: "Transaction Date", key: "transactionDate" },
    { displayName: "Transaction Amount", key: "txnAmount" },
    { displayName: "Status", key: "status" },
    { displayName: "Remarks", key: "remarks" },
  ];

  const filteredItems = useMemo(() => {
    return Array.isArray(fundManagementReport) && fundManagementReport.length > 0
      ? fundManagementReport.filter(item => {
        if (!searchItem) return true;
        return Object.values(item).some(val => typeof val === "string" && val.toLowerCase().includes(searchItem.toLowerCase()));
      })
      : [];
  }, [fundManagementReport, searchItem]);


  const rowData = useMemo(() => {
    return (filteredItems || []).map((item) => {
      return {
        aCode: item?.ACode,
        dealerId: item?.DealerId,
        transactionType: item?.TransactionType,
        txnid: item?.TransactionID,
        transactionDate: moment(item?.TransactionDate).format(dateFormat),
        txnAmount: defaultCurrency + "" + item?.TxnAmount,
        status: (
          <span className={`badge ${item?.Status.toLowerCase() === "success" ? "bg-success" : "bg-danger"}`}>{item?.Status}</span>
        ),
        remarks: item?.Remarks,
      };
    });
  }, [filteredItems, defaultCurrency]);


  const onSubmit = (data) => {
    dispatch(getFundManagementReports({
      ...data,
      "distributorId": Number(data?.distributorId || 0),
    }))
  };


  const handleReset = () => {
    reset({
      distributorId: 0,
      txnId: "",
      fromDate: formatDate(beforeTwoDays),
      toDate: formatDate(today),
    });
    setSearchItem("");
    dispatch(resetMessage());
    getActivationReportFun()
  };


  const handleExportExcel = () => {
    const data = filteredItems.map((item) => ({
      "A Code": item?.ACode,
      "Dealer ID": item?.DealerId,
      "Transaction ID": item?.TransactionID,
      "Transaction Type": item?.TransactionType,
      "Transaction Date": moment(item?.TransactionDate).format(dateFormat),
      "Transaction Amount": defaultCurrency + "" + item?.TxnAmount,
      "Status": item?.Status,
      "Remarks": item?.Remarks,
    }));


    downloadExcelSampleFile(data, "fund-management-report.xlsx");
  };


  return (
    <>
      <CardLayout
        title="Fund Management Report"
        icon="fas fa-file-invoice-dollar"
        rightContent={
          <div className="d-flex flex-wrap align-items-center gap-1">
            <input
              type="search"
              style={{ width: "300px", }}
              onChange={(e) => setSearchItem(e.target.value)}
              className="form-control form-control-sm"
              placeholder="Search..."
              value={searchItem}
            />
            <Button
              title="Excel"
              buttonType="button"
              variant="secondary"
              onClick={handleExportExcel}
              icon={<i className="ri-download-cloud-line me-2" />}
            />
          </div>
        }
      >
        <>
          <div>
            {/* Body */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <div
                className="row" >
                {/* ACode */}
                <div className="col-lg-2 col-md-6">
                  <label className="form-label fw-semibold">ACode</label>
                  <select
                    className="form-select"
                    {...register("distributorId")} >
                    <option value="0">All</option>
                    {aCodeList?.map((item) => (
                      <option
                        key={item.Id}
                        value={item.Id} >
                        {item.ACode}
                      </option>
                    ))}
                  </select>
                </div>

                {/* TxnID */}
                <div className="col-lg-3 col-md-6">
                  <label className="form-label fw-semibold">TxnID</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="TxnID"
                    {...register("txnId")}
                  />
                </div>

                {/* From Date */}
                <div className="col-lg-2 col-md-6">
                  <label className="form-label fw-semibold">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("fromDate")}
                  />
                </div>

                {/* To Date */}
                <div className="col-lg-2 col-md-6">
                  <label className="form-label fw-semibold">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("toDate")}
                  />
                </div>

                <div className="col-lg-3 col-md-6 d-flex align-items-end gap-3" style={{ height: '70px' }}>
                  {/* Buttons */}
                  <Button
                    title="Get Report"
                    buttonType="submit"
                    variant="primary"
                    disable={transactionReportsLoading}
                    loading={transactionReportsLoading}
                    icon={<i className="ri-search-line me-2"></i>}
                    style={transactionReportsLoading ? { width: '46.5%', padding: '10px 2px' } : {}}
                  />

                  <Button
                    title="Reset"
                    buttonType="button"
                    variant="secondary"
                    onClick={handleReset}
                    icon={<i className="ri-refresh-line me-2"></i>}
                  />
                </div>

              </div>
            </form>
          </div>
          {/* Table */}
          <div className="mt-2">
            <Table
              columns={columns}
              data={rowData}
              pagination
              loading={transactionReportsLoading}
              tableHeight="335px"
            />
          </div>

        </>
      </CardLayout>
    </>
  );
};

export default FundManagementReport;
